import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { DashboardLayout } from "./Dashboard";
import { cn } from "@/lib/utils";
import { getUserMe, createApiKey, revokeApiKey, type ApiKey, ApiError } from "@/lib/api";
import {
    Key,
    Plus,
    Copy,
    Check,
    AlertTriangle,
    Lightbulb,
    Shield,
    Trash2,
    X,
    Loader2,
    CheckCircle,
    AlertCircle
} from "lucide-react";

export function APIKeysPage() {
    const { getToken } = useAuth();
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newKeyName, setNewKeyName] = useState("");
    const [newKeyEnv, setNewKeyEnv] = useState<"live" | "test">("live");
    const [createdKey, setCreatedKey] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    // Fetch API keys
    const fetchKeys = useCallback(async () => {
        try {
            setError(null);
            const data = await getUserMe(getToken);
            setApiKeys(data.api_keys);
        } catch (err) {
            console.error("Failed to fetch API keys:", err);
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError("Failed to load API keys. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    }, [getToken]);

    useEffect(() => {
        fetchKeys();
    }, [fetchKeys]);

    const handleCreateKey = async () => {
        if (!newKeyName.trim()) return;
        setIsCreating(true);
        setError(null);

        try {
            const result = await createApiKey(getToken, newKeyName.trim(), newKeyEnv);
            setCreatedKey(result.api_key.key);

            // Add the new key to the list (without the full key)
            setApiKeys((prev) => [
                {
                    id: result.api_key.id,
                    key_prefix: result.api_key.key_prefix,
                    name: result.api_key.name,
                    environment: result.api_key.environment,
                    status: result.api_key.status,
                    created_at: result.api_key.created_at,
                    last_used_at: null,
                },
                ...prev,
            ]);
            setNewKeyName("");
            setShowCreateForm(false);
        } catch (err) {
            console.error("Failed to create API key:", err);
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError("Failed to create API key. Please try again.");
            }
        } finally {
            setIsCreating(false);
        }
    };

    const handleRevokeKey = async (keyId: string) => {
        if (!confirm("Are you sure you want to revoke this key? This cannot be undone.")) {
            return;
        }

        try {
            setError(null);
            await revokeApiKey(getToken, keyId);
            // Remove from list
            setApiKeys((prev) => prev.filter((k) => k.id !== keyId));
        } catch (err) {
            console.error("Failed to revoke API key:", err);
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError("Failed to revoke API key. Please try again.");
            }
        }
    };

    const copyToClipboard = async (text: string, id: string = "new") => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(id);
            setTimeout(() => setCopiedKey(null), 2000);
        } catch (err) {
            console.error("Failed to copy:", err);
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const formatLastUsed = (dateStr: string | null) => {
        if (!dateStr) return "Never";
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return "Just now";
        if (diffMins < 60) return `${diffMins} min ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
        return formatDate(dateStr);
    };

    return (
        <DashboardLayout>
            {/* Header */}
            <header className="bg-transparent px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">API Keys</h1>
                        <p className="text-sm sm:text-base text-slate-500">Manage your API keys and access tokens</p>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 shadow-sm w-full sm:w-auto"
                    >
                        <Plus className="w-4 h-4" />
                        Create new key
                    </button>
                </div>
            </header>

            <div className="px-4 sm:px-6 lg:px-8 pb-8 lg:pb-12">
                {/* Error Alert */}
                {error && (
                    <div className="mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                        <div className="p-1 bg-red-100 rounded-full text-red-600 flex-shrink-0">
                            <AlertCircle className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-red-900">Error</p>
                            <p className="text-sm text-red-700 break-words">{error}</p>
                        </div>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-600 hover:text-red-800 flex-shrink-0"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* New Key Created Alert */}
                {createdKey && (
                    <div className="mb-6 p-3 sm:p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <div className="flex items-start gap-3">
                            <div className="p-1 bg-emerald-100 rounded-full text-emerald-600 flex-shrink-0">
                                <CheckCircle className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-emerald-900">
                                    New API key created!
                                </p>
                                <p className="text-sm text-emerald-700 mb-3">
                                    Copy this key now. You won't be able to see it again.
                                </p>
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                    <div className="relative group flex-1 min-w-0">
                                        <code className="text-xs sm:text-sm bg-white border border-emerald-200 px-2 sm:px-3 py-1.5 rounded-lg font-mono text-emerald-800 shadow-sm flex items-center break-all">
                                            {createdKey}
                                        </code>
                                    </div>
                                    <button
                                        onClick={() => copyToClipboard(createdKey, "new")}
                                        className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 shadow-sm flex-shrink-0"
                                    >
                                        {copiedKey === "new" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                        {copiedKey === "new" ? "Copied" : "Copy"}
                                    </button>
                                </div>
                            </div>
                            <button
                                onClick={() => setCreatedKey(null)}
                                className="text-emerald-600 hover:text-emerald-800 p-1 hover:bg-emerald-100 rounded transition-colors flex-shrink-0"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Security Notice */}
                <div className="mb-6 lg:mb-8 p-3 sm:p-4 bg-sky-50 border border-sky-100 rounded-xl flex items-start gap-3">
                    <div className="p-1 bg-sky-100 rounded-full text-sky-600 flex-shrink-0">
                        <Shield className="w-4 h-4" />
                    </div>
                    <div>
                        <p className="font-medium text-sky-900 text-sm sm:text-base">Keep your keys secure</p>
                        <p className="text-xs sm:text-sm text-sky-700">
                            Never commit API keys to version control. Use environment variables instead.
                        </p>
                    </div>
                </div>

                {/* Keys - Desktop Table / Mobile Cards */}
                <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)] overflow-hidden">
                    {isLoading ? (
                        <div className="p-8 sm:p-12 text-center">
                            <Loader2 className="w-8 h-8 text-sky-500 animate-spin mx-auto mb-3" />
                            <p className="text-slate-500">Loading API keys...</p>
                        </div>
                    ) : apiKeys.length === 0 ? (
                        <div className="p-8 sm:p-12 text-center flex flex-col items-center">
                            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4">
                                <Key className="w-7 h-7 sm:w-8 sm:h-8 text-slate-300" />
                            </div>
                            <h3 className="text-slate-900 font-medium text-base sm:text-lg mb-1">No API keys yet</h3>
                            <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
                                Create your first API key to start building with our weather data.
                            </p>
                            <button
                                onClick={() => setShowCreateForm(true)}
                                className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm"
                            >
                                Create API Key
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table - Hidden on mobile */}
                            <div className="hidden lg:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-slate-100 bg-slate-50/50">
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Key Prefix
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Name
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Created
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Last Used
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {apiKeys.map((key) => (
                                            <tr
                                                key={key.id}
                                                className="hover:bg-slate-50/80 transition-colors group"
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <code className="text-sm text-slate-700 bg-slate-100 px-2 py-1 rounded-md font-mono border border-slate-200">
                                                            {key.key_prefix}••••••••
                                                        </code>
                                                        <button
                                                            onClick={() => copyToClipboard(key.key_prefix, key.id)}
                                                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded transition-colors opacity-0 group-hover:opacity-100"
                                                            title="Copy prefix"
                                                        >
                                                            {copiedKey === key.id ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 font-medium text-slate-900">
                                                    {key.name}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-500">
                                                    {formatDate(key.created_at)}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-500">
                                                    {formatLastUsed(key.last_used_at)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span
                                                        className={cn(
                                                            "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border",
                                                            key.status === "active"
                                                                ? key.environment === "live"
                                                                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                                                    : "bg-amber-50 text-amber-700 border-amber-100"
                                                                : "bg-slate-100 text-slate-500 border-slate-200"
                                                        )}
                                                    >
                                                        <span className={cn("w-1.5 h-1.5 rounded-full",
                                                            key.status === "active"
                                                                ? key.environment === "live" ? "bg-emerald-500" : "bg-amber-500"
                                                                : "bg-slate-400"
                                                        )} />
                                                        {key.status === "revoked"
                                                            ? "Revoked"
                                                            : key.environment === "live"
                                                                ? "Active"
                                                                : "Test Mode"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    {key.status === "active" && (
                                                        <button
                                                            onClick={() => handleRevokeKey(key.id)}
                                                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Revoke Key"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards - Hidden on desktop */}
                            <div className="lg:hidden divide-y divide-slate-100">
                                {apiKeys.map((key) => (
                                    <div key={key.id} className="p-4 hover:bg-slate-50/50 transition-colors">
                                        <div className="flex items-start justify-between gap-3 mb-3">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium text-slate-900 truncate">{key.name}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <code className="text-xs text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded font-mono border border-slate-200">
                                                        {key.key_prefix}••••
                                                    </code>
                                                    <button
                                                        onClick={() => copyToClipboard(key.key_prefix, key.id)}
                                                        className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                                                    >
                                                        {copiedKey === key.id ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                                    </button>
                                                </div>
                                            </div>
                                            <span
                                                className={cn(
                                                    "inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full border flex-shrink-0",
                                                    key.status === "active"
                                                        ? key.environment === "live"
                                                            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                                            : "bg-amber-50 text-amber-700 border-amber-100"
                                                        : "bg-slate-100 text-slate-500 border-slate-200"
                                                )}
                                            >
                                                <span className={cn("w-1.5 h-1.5 rounded-full",
                                                    key.status === "active"
                                                        ? key.environment === "live" ? "bg-emerald-500" : "bg-amber-500"
                                                        : "bg-slate-400"
                                                )} />
                                                {key.status === "revoked"
                                                    ? "Revoked"
                                                    : key.environment === "live"
                                                        ? "Live"
                                                        : "Test"}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-xs text-slate-500">
                                            <div className="flex items-center gap-3">
                                                <span>Created {formatDate(key.created_at)}</span>
                                                <span>•</span>
                                                <span>Used {formatLastUsed(key.last_used_at)}</span>
                                            </div>
                                            {key.status === "active" && (
                                                <button
                                                    onClick={() => handleRevokeKey(key.id)}
                                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Revoke Key"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Create New Key Form Modal - Simple inline implementation */}
                {showCreateForm && (
                    <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
                        <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full sm:max-w-md overflow-hidden animate-in fade-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200">
                            <div className="px-4 sm:px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                                <h3 className="font-semibold text-slate-900">Create New API Key</h3>
                                <button
                                    onClick={() => setShowCreateForm(false)}
                                    className="text-slate-400 hover:text-slate-600 p-1"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-4 sm:p-6 space-y-4 pb-8 sm:pb-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Key Name
                                    </label>
                                    <input
                                        type="text"
                                        value={newKeyName}
                                        onChange={(e) => setNewKeyName(e.target.value)}
                                        placeholder="e.g., Production Server"
                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Environment
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setNewKeyEnv("live")}
                                            className={cn(
                                                "px-3 py-2.5 rounded-lg text-sm font-medium border text-left transition-all",
                                                newKeyEnv === "live"
                                                    ? "bg-sky-50 border-sky-200 text-sky-700 ring-1 ring-sky-200"
                                                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                            )}
                                        >
                                            <div className="font-semibold mb-0.5">Live</div>
                                            <div className="text-xs opacity-80">For production use</div>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setNewKeyEnv("test")}
                                            className={cn(
                                                "px-3 py-2.5 rounded-lg text-sm font-medium border text-left transition-all",
                                                newKeyEnv === "test"
                                                    ? "bg-amber-50 border-amber-200 text-amber-700 ring-1 ring-amber-200"
                                                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                                            )}
                                        >
                                            <div className="font-semibold mb-0.5">Test</div>
                                            <div className="text-xs opacity-80">For development</div>
                                        </button>
                                    </div>
                                </div>

                                <div className="pt-2 flex gap-3">
                                    <button
                                        onClick={() => setShowCreateForm(false)}
                                        className="flex-1 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleCreateKey}
                                        disabled={isCreating || !newKeyName.trim()}
                                        className="flex-1 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                                    >
                                        {isCreating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                        {isCreating ? "Creating..." : "Create Key"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}

export default APIKeysPage;
